/**
 * The sign-in screen.
 *
 * It names the development accounts when the deployment is not in production, because a console
 * nobody can get into is not a demonstrable console, and hiding credentials that are already in
 * the source helps nobody. In production the hint disappears with the accounts themselves.
 *
 * Where single sign-on is configured, it is offered above the password form and the provider is
 * named. The local form stays: an identity provider that is down must not be the reason nobody can
 * reach the platform during the incident that took it down.
 */

import { useState, type FormEvent } from 'react'
import { api } from '../api'
import { Note } from '../components/ui'

export default function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [env, setEnv] = useState<string>('')
  const [sso, setSso] = useState<{ enabled: boolean; provider: string } | null>(null)

  // The health endpoint is open, so the environment banner can be shown before signing in.
  useState(() => {
    api
      .get<{ env: string }>('/health')
      .then((h) => setEnv(h.env))
      .catch(() => {})
    api
      .get<{ enabled: boolean; provider: string }>('/auth/oidc')
      .then(setSso)
      .catch(() => {})
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    api
      .post('/auth/login', { username, password })
      .then(onSignedIn)
      .catch((err: Error) => setError(err.message))
      .finally(() => setBusy(false))
  }

  return (
    <div className="login">
      <div className="box">
        <div className="brand">
          <svg width="26" height="26" viewBox="0 0 32 32" aria-hidden>
            <path d="M16 2 4 7v9c0 7.5 5.1 13.4 12 14.9 6.9-1.5 12-7.4 12-14.9V7L16 2Z" fill="var(--accent)" />
            <path d="M16 8v14M10 12v8M22 12v8" stroke="var(--surface)" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
          Bastion
        </div>
        <div className="tagline">Security operations — ingestion, detection, response</div>

        {sso?.enabled && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="card-body">
              <a
                className="primary"
                href="/api/auth/oidc/start"
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  display: 'inline-flex',
                  textDecoration: 'none',
                }}
              >
                Sign in with your organization account
              </a>
              {/* Named rather than described, so somebody who is sent to the wrong provider can
                  tell before they type a password into it. */}
              <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>{sso.provider}</div>
            </div>
          </div>
        )}

        <form className="card" onSubmit={submit}>
          <div className="card-body">
            {error && <div style={{ marginBottom: 12 }}><Note tone="danger">{error}</Note></div>}
            <div className="field">
              <label htmlFor="u">Username</label>
              <input
                id="u"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="p">Password</label>
              <input
                id="p"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button className="primary" type="submit" disabled={busy || !username || !password} style={{ width: '100%', justifyContent: 'center' }}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </form>

        {env && env !== 'prod' && (
          <div style={{ marginTop: 14 }}>
            <Note>
              <strong>Development deployment.</strong> Seeded accounts, all with password{' '}
              <code>bastion</code>: <code>admin</code> (platform admin), <code>lead</code> (can
              approve), <code>analyst</code>, <code>viewer</code> (read-only), and{' '}
              <code>globex</code> in a second workspace.
            </Note>
          </div>
        )}
      </div>
    </div>
  )
}
